import { useEffect, useState } from "react";
import { useOutletContext, useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/Button";
import { DateRangePicker } from "@/components/DateRangePicker";
import { EmptyState } from "@/components/EmptyState";
import { LoadError } from "@/components/LoadError";
import { Pagination } from "@/components/Pagination";
import { SearchField } from "@/components/SearchField";
import { Select } from "@/components/Select";
import { useApi } from "@/lib/api";
import { localDayEnd, localDayStart } from "@/lib/calendarDates";
import { formatDateTime } from "@/lib/formatDateTime";
import { POLL_INTERVAL_MS } from "@/lib/onlineStatus";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import type { LogsLayoutContext } from "./LogsLayout";

interface AuditLogEntry {
  id: string;
  createdAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  eventType: string;
  description: string;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  userAvatarId: string | null;
}

interface OrgUser {
  id: string;
  name: string;
  email: string;
}

const COLUMNS = ["When", "Activity", "User", "Source"] as const;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 6 }, (_, row) => (
        <tr key={row} className="h-14 animate-pulse">
          <td className="py-2 pr-4">
            <div className="h-3 w-36 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-64 rounded bg-surface" />
          </td>
          <td className="py-2 pr-4">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 shrink-0 rounded-full bg-surface" />
              <div className="h-3 w-28 rounded bg-surface" />
            </div>
          </td>
          <td className="py-2 pr-4">
            <div className="h-3 w-28 rounded bg-surface" />
            <div className="mt-1.5 h-2.5 w-36 rounded bg-surface" />
          </td>
        </tr>
      ))}
    </>
  );
}

export function AuditLog() {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId } = useParams();
  const { setSummary } = useOutletContext<LogsLayoutContext>();

  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [userIdFilter, setUserIdFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [orgUsers, setOrgUsers] = useState<OrgUser[]>([]);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    async function loadEntries() {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });

      if (debouncedSearch) params.set("search", debouncedSearch);
      if (userIdFilter) params.set("userId", userIdFilter);
      const from = localDayStart(dateFrom);
      if (from) params.set("from", from);
      const to = localDayEnd(dateTo);
      if (to) params.set("to", to);

      const data = await api.get<PagedResult<AuditLogEntry>>(
        `/organizations/${orgId}/audit-logs?${params.toString()}`,
      );

      if (cancelled) {
        return;
      }

      if (data) {
        setEntries(data.items);
        setTotalCount(data.totalCount);
        setError(null);
      } else {
        setError("Couldn't load audit logs.");
      }
    }

    void loadEntries();

    const interval = setInterval(() => void loadEntries(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    accessToken,
    api,
    orgId,
    page,
    debouncedSearch,
    userIdFilter,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    void api
      .get<PagedResult<OrgUser>>(
        `/organizations/${orgId}/users?page=1&pageSize=100`,
      )
      .then((data) => {
        if (!cancelled) {
          setOrgUsers(data?.items ?? []);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId]);

  useEffect(() => {
    if (!entries) {
      return;
    }

    setSummary({ total: totalCount, noun: "event" });

    return () => setSummary(null);
  }, [entries, totalCount, setSummary]);

  const hasFilters = Boolean(
    debouncedSearch || userIdFilter || dateFrom || dateTo,
  );

  function clearFilters() {
    setSearch("");
    setUserIdFilter("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }

  const isEmpty = entries !== null && totalCount === 0;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <SearchField
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          label="Search the audit log"
          placeholder="Search activity, user, or IP address"
        />
        <Select
          value={userIdFilter}
          onChange={(e) => {
            setUserIdFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All users</option>
          {orgUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={(newFrom, newTo) => {
            setDateFrom(newFrom);
            setDateTo(newTo);
            setPage(1);
          }}
        />
      </div>

      {error && !entries && (
        <LoadError
          what="the audit log"
          onRetry={() => setPage(1)}
          className="mb-4"
        />
      )}

      {isEmpty ? (
        <EmptyState
          title={
            hasFilters ? "No events match these filters" : "No activity yet"
          }
          description={
            hasFilters
              ? "Try a different search, user, or date range."
              : "Changes to users, teams, resources, and sessions appear here."
          }
        >
          {hasFilters && <Button onClick={clearFilters}>Clear filters</Button>}
        </EmptyState>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[56rem] text-left text-sm">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column}
                    scope="col"
                    className="border-b border-surface pb-2.5 pr-4 text-xs font-semibold uppercase tracking-wider text-foreground/70"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!entries ? (
                <SkeletonRows />
              ) : (
                entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="h-14 border-b border-surface/70 hover:bg-surface"
                  >
                    <td className="whitespace-nowrap py-2 pr-4 tabular-nums text-foreground/70">
                      {formatDateTime(entry.createdAt)}
                    </td>
                    <td className="max-w-2xl py-2 pr-4 wrap-anywhere">
                      {entry.description}
                    </td>
                    <td className="py-2 pr-4">
                      {(entry.userName ?? entry.userEmail) ? (
                        <div className="flex items-center gap-2.5">
                          <Avatar
                            name={entry.userName}
                            email={entry.userEmail}
                            avatarId={entry.userAvatarId}
                            size="md"
                          />
                          <span className="truncate text-foreground/70">
                            {entry.userName ?? entry.userEmail}
                          </span>
                        </div>
                      ) : (
                        <span className="text-foreground/70">System</span>
                      )}
                    </td>
                    <td className="max-w-xs py-2 pr-4 text-foreground/70 wrap-anywhere">
                      {!entry.ipAddress && !entry.userAgent ? (
                        <span className="block">Tendra</span>
                      ) : (
                        <>
                          <span className="block tabular-nums">
                            {entry.ipAddress ?? "Unknown"}
                          </span>
                          <span className="block break-all text-xs">
                            {entry.userAgent ?? "Unknown"}
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!isEmpty && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalCount / PAGE_SIZE)}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
